import { Component, effect, inject } from '@angular/core';
import { HeaderComponent } from '../../atoms/header/header.component';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { DataLayerType } from '../../../models/data-layer-type';
import { getState, patchState } from '@ngrx/signals';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { TypeDropdownComponent } from '../../atoms/type-dropdown/type-dropdown.component';
import { DataLayer } from '../../../models/data-layer';
import { LayerDropdownComponent } from '../../atoms/layer-dropdown/layer-dropdown.component';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { IndicatorDataService } from '../../../services/data/indicator-data.service';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { ImportValidatorService } from '../../../services/data/import-validator.service';
import { DividerModule } from 'primeng/divider';
import { AlertStyleDirective } from '../../../directives/alert-style.directive';
import { ImportData, ImportDataValidation } from '../../../models/import-data';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { ToastModule } from 'primeng/toast';
import { IndicatorPickListComponent } from '../../molecules/indicator-pick-list/indicator-pick-list.component';
import { TriStateCheckboxModule } from 'primeng/tristatecheckbox';
import { DataImportListComponent } from '../../molecules/data-import-list/data-import-list.component';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { Severity } from '../../../models/severity';
import { PanelModule } from 'primeng/panel';
import {
  DataExportMode,
  ExportHelperService,
} from '../../../services/data/export-helper.service';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { DropdownItem } from '../../../models/form/basic';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { CheckboxModule } from 'primeng/checkbox';
import { DataLayerTypeEntryComponent } from '../../molecules/data-layer-type-entry/data-layer-type-entry.component';
import { TabViewChangeEvent, TabViewModule } from 'primeng/tabview';
import { ImportValidationResultDataComponent } from '../../molecules/import-validation-result-data/import-validation-result-data.component';
import { IndicatorData } from '../../../models/indicator-data';
import { GeoData, KeyValueDistricts } from '../../../models/geo-data';
import { sanitizeFilename } from '../../../utility/app.utility';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-import-export-data',
  standalone: true,
  imports: [
    HeaderComponent,
    NgIf,
    DropdownModule,
    FormsModule,
    TypeDropdownComponent,
    LayerDropdownComponent,
    ButtonModule,
    FileUploadModule,
    DividerModule,
    AlertStyleDirective,
    NgClass,
    MaskComponent,
    SidebarModule,
    ToastModule,
    IndicatorPickListComponent,
    TriStateCheckboxModule,
    DataImportListComponent,
    NgForOf,
    PanelModule,
    AlertComponent,
    FormFieldWrapperComponent,
    CheckboxModule,
    DataLayerTypeEntryComponent,
    TabViewModule,
    ImportValidationResultDataComponent,
  ],
  templateUrl: './import-export-data.component.html',
  styleUrl: './import-export-data.component.scss',
})
export class ImportExportDataComponent {
  readonly layerTypeStore = inject(DataLayerTypeStore);
  readonly layerStore = inject(DataLayerStore);
  readonly globalStore = inject(GlobalsStore);
  readonly geoDataStore = inject(GeoDataStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);

  selectedType: DataLayerType | null = null;
  selectedLayer: DataLayer | null = null;
  exportSorting?: 'key' | 'name' = undefined;
  exportOrder?: 'asc' | 'desc' = undefined;
  importValidationResult?: ImportDataValidation;
  importValidationResults?: { [key: string]: ImportDataValidation };
  validationYears?: string[];
  dataToShowInOverview: ImportData[] = [];
  icon = '';
  activeExportSelection = false;
  multipleYears = false;
  missingYears: string[] = [];
  featuresToAdd?: GeoData;

  sortingOptions: DropdownItem[] = [
    {
      value: null,
      label: 'nicht sortieren',
    },
    {
      value: 'name',
      label: 'Name',
    },
    {
      value: 'key',
      label: 'Schlüssel',
    },
  ];

  orderOptions: DropdownItem[] = [
    {
      value: 'asc',
      label: 'aufsteigend',
      icon: 'fas fa-arrow-up',
    },
    {
      value: 'desc',
      label: 'absteigend',
      icon: 'fas fa-arrow-down',
    },
  ];

  constructor(
    private messageService: MessageService,
    protected indicatorDataService: IndicatorDataService,
    protected importValidatorService: ImportValidatorService,
    private exportHelper: ExportHelperService,
    private permissionService: PermissionService
  ) {
    effect(() => {
      const globalState = getState(this.globalStore);
      const geoState = getState(this.geoDataStore);
      this.selectedType = globalState.selectedType;
      this.selectedLayer = globalState.selectedLayer;

      if (geoState.trigger && geoState.trigger.includes('linkDataLayer')) {
        setTimeout(() => {
          this.linkNewGeoDataToLayer();
        });
      }
    });
  }

  /**
   * Determines whether the user has permission to import data.
   *
   * @return {boolean} True if the user has the necessary permission to import data, otherwise false.
   */
  canImport() {
    return this.permissionService.canImportData();
  }

  /**
   * Determines whether the user has the required permissions to export data.
   *
   * @return {boolean} True if the user can export data, otherwise false.
   */
  canExport() {
    return this.permissionService.canExportData();
  }

  /**
   * Resets the import section by clearing and reinitializing related properties.
   *
   * This method sets the following:
   * - `importValidationResult` to `undefined`
   * - `dataToShowInOverview` to an empty array
   * - `icon` to an empty string
   * - `featuresToAdd` to `undefined`
   * - `missingYears` to an empty array
   *
   * @return {void} This method does not return a value.
   */
  resetImportSection(): void {
    this.importValidationResult = undefined;
    this.dataToShowInOverview = [];
    this.icon = '';
    this.featuresToAdd = undefined;
    this.missingYears = [];
  }

  /**
   * Handles the upload event, processes the uploaded data, validates it, and updates the relevant state.
   *
   * @param {Object} event The event object containing the uploaded data. The `event.originalEvent.body`
   * represents the content of the uploaded file to process.
   * @return {void} This method does not return a value but updates internal states
   * such as import validation results, missing years, and messages based on the success or failure
   * of the upload and validation process.
   */
  onUpload(event: any): void {
    const importResults = event.originalEvent.body;
    if (!importResults || !this.selectedType) {
      this.messageService.add({
        severity: 'danger',
        summary: 'Upload fehlgeschlagen!',
        detail:
          'Der Upload war nicht erfolgreich. Überprüfen Sie das Dateiformat.',
      });
      return;
    }

    if (this.multipleYears) {
      this.importValidationResults = {};
      for (const year in importResults) {
        if (year === 'file') {
          delete importResults[year];
          continue;
        }
        const layerToImport = this.layerStore.filterByParentAndYear(
          this.selectedType._id,
          +year
        );
        if (!layerToImport) {
          this.missingYears.push(year);
          delete importResults[year];
          continue;
        }
        const yearImportValues = importResults[year];
        this.importValidationResults[year] =
          this.importValidatorService.validateImportData(
            yearImportValues,
            layerToImport
          );
      }
      this.validationYears = Object.keys(importResults);
      this.importValidationResult =
        this.importValidationResults[this.validationYears[0]];
    } else if (this.selectedLayer) {
      const layerToImport = this.globalStore.selectedLayer();
      if (!layerToImport) return;
      this.importValidationResult =
        this.importValidatorService.validateImportData(
          importResults,
          layerToImport
        );

      console.log('this.importValidationResult', this.importValidationResult);
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Upload erfolgreich!',
    });
  }

  /**
   * Retrieves an array of data layer types.
   *
   * @return {DataLayerType[]} An array containing all available data layer types.
   */
  get typeSelectItems(): DataLayerType[] {
    return this.layerTypeStore.asArray();
  }

  /**
   * Updates the items to be displayed in an overview based on the provided state.
   *
   * @param {Severity} state - The severity state used to filter items for display in the overview.
   * @return {void} No return value; the method modifies the internal state of the object.
   */
  setItemsToShow(state: Severity): void {
    this.dataToShowInOverview = this.importValidationResult
      ? this.importValidationResult.preview[state]
      : [];
    this.setIconByState(state);
  }

  /**
   * Sets the year to display based on the selected tab event.
   *
   * @param {TabViewChangeEvent} event - The event triggered by the tab view change, containing the index of the selected tab.
   * @return {void} This method does not return a value.
   */
  setYearToShow(event: TabViewChangeEvent): void {
    if (!this.validationYears || !this.importValidationResults) return;
    const yearToShow = this.validationYears[event.index];
    this.importValidationResult = this.importValidationResults[yearToShow];
  }

  /**
   * Closes the overview by resetting the displayed data and icon.
   *
   * @return {void} Doesn't return any value.
   */
  closeOverview(): void {
    this.dataToShowInOverview = [];
    this.icon = '';
  }

  /**
   * Closes the export selection by deactivating it.
   *
   * @return {void} This method does not return a value.
   */
  closeExportSelection(): void {
    this.activeExportSelection = false;
  }

  /**
   * Handles the import process by validating and processing the import data.
   * Updates the data store with the imported data and displays appropriate messages
   * based on the success or failure of the operation.
   *
   * @return {void} No return value.
   */
  import(): void {
    try {
      const dataToImport = this.getImportData();
      if (!dataToImport || !this.importValidationResult) return;

      this.indicatorDataStore.updateMultiple({ items: dataToImport });
      this.messageService.add({
        severity: 'info',
        summary: 'Import erfolgreich!',
      });
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
      });
    }
    this.resetImportSection();
  }

  /**
   * Retrieves the processed import data based on the selected type and validation results.
   *
   * @return {IndicatorData[] | undefined} The processed import data array containing indicator data objects
   *                                       if valid data is available, or undefined if the selected type
   *                                       is not specified or validation data is unavailable.
   */
  private getImportData(): IndicatorData[] | undefined {
    if (!this.selectedType) return undefined;
    const geoType = this.selectedType.type;
    const isPointType = geoType === 'Point';
    let featuresToAdd: KeyValueDistricts = {};

    if (this.multipleYears) {
      let allYearData: IndicatorData[] = [];
      for (const year in this.importValidationResults) {
        const result = this.importValidationResults[year];
        const previewData = result.preview;
        const importData: IndicatorData[] =
          this.importValidatorService.convertToImportDataToIndicatorData([
            ...previewData.success,
            ...previewData.info,
            ...previewData.warning,
            ...previewData.error,
          ]);
        allYearData = allYearData.concat(importData);

        if (result.hints.noGeoData) {
          if (isPointType) {
            this.addMissingFeatures(geoType, result.hints.featuresToAdd);
          } else {
            featuresToAdd = { ...result.hints.featuresToAdd };
          }
        }
      }
      if (!isPointType) this.addMissingFeatures(geoType, featuresToAdd);

      return allYearData;
    } else {
      if (!this.importValidationResult) return;
      this.addMissingFeatures(
        geoType,
        this.importValidationResult.hints.featuresToAdd
      );
      const previewData = this.importValidationResult.preview;
      return this.importValidatorService.convertToImportDataToIndicatorData([
        ...previewData.success,
        ...previewData.info,
        ...previewData.warning,
        ...previewData.error,
      ]);
    }
  }

  /**
   * Adds missing geographic features of the specified type to the geo data store.
   *
   * @param {('Point' | 'Polygon')} type The type of geographical feature to add, either 'Point' or 'Polygon'.
   * @param {KeyValueDistricts} featuresToAdd The features to add, represented as a key-value mapping of districts.
   * @return {void} This method doesn't return a value.
   */
  private addMissingFeatures(
    type: 'Point' | 'Polygon',
    featuresToAdd: KeyValueDistricts
  ): void {
    if (!featuresToAdd || !Object.keys(featuresToAdd).length) return;
    this.geoDataStore.addItem(this.geoDataStore.generate(featuresToAdd, type));
    // trigger
    patchState(this.geoDataStore, {
      triggerOptions: {
        dataLayer: this.selectedLayer,
        multipleYears: this.multipleYears,
        validationYears: this.validationYears,
      },
    });
  }

  /**
   * Links newly uploaded geographic data to relevant layers based on their type and configuration.
   * Updates the geoData property of the layers and synchronizes changes in the store.
   * Layers to update are identified based on their type, years, or specific selection.
   *
   * @return {void} No return value. This method performs updates on the layer store and the global store.
   */
  linkNewGeoDataToLayer(): void {
    const layerType = this.globalStore.selectedType();
    if (!layerType) return; // warn...
    const typeId = layerType?._id;
    let layersToUpdate: DataLayer[] = [];
    if (layerType.type === 'Polygon') {
      // all polygon-layer
      layersToUpdate = this.layerStore.filterByParent(typeId);
      layersToUpdate.map(layer => {
        layer.geoData = this.geoDataStore.lastPersistedId();
      });
    } else if (this.multipleYears && this.validationYears) {
      // multiple poi-layer // todo: same geo-data each poi-layer - test new geo-upload
      layersToUpdate = this.layerStore.filterByParentAndYears(
        typeId,
        this.validationYears.map(year => +year)
      );
      layersToUpdate.map(layer => {
        layer.geoData = this.geoDataStore.lastPersistedId();
      });
    } else if (this.selectedLayer) {
      // single poi layer
      const layer = this.selectedLayer;
      layer.geoData = this.geoDataStore.lastPersistedId();
      layersToUpdate = [layer];
    }

    this.layerStore.updateMultiple({
      items: layersToUpdate,
      message: { avoid: true },
    });
    this.globalStore.setSelectedGeoData(
      this.geoDataStore.items()[this.geoDataStore.lastPersistedId()]
    );
  }

  /**
   * Downloads a template by exporting data with empty fields included.
   *
   * @return {void} Does not return a value.
   */
  downloadTemplate(): void {
    this.exportData(DataExportMode.IncludeEmpty);
  }

  /**
   * Exports data to an Excel file based on the specified mode.
   *
   * @param {DataExportMode} mode - The mode used to determine how the data is exported.
   * @return {void} This method does not return anything.
   */
  exportData(mode: DataExportMode): void {
    this.indicatorDataStore.convertToXlsx({
      exportData: this.exportHelper.buildIndicatorDataExportArray(
        mode,
        this.exportSorting,
        this.exportOrder
      ),
      filename: sanitizeFilename(
        `${this.selectedType?.name}_${this.selectedLayer?.year}`
      ),
    });
    this.closeExportSelection();
  }

  /**
   * Sets the icon property based on the provided state.
   * Updates the icon to a corresponding font-awesome class depending on the severity.
   *
   * @param {Severity} state - Represents the severity state, which can be one of the following: 'success', 'info', 'warning', 'error', 'lock', or others.
   * @return {void} Does not return a value as it directly updates the icon property.
   */
  setIconByState(state: Severity): void {
    switch (state) {
      case 'success':
        this.icon = 'fas fa-circle-plus';
        break;
      case 'info':
        this.icon = 'fas fa-save';
        break;
      case 'warning':
        this.icon = 'fas fa-exclamation-triangle';
        break;
      case 'error':
        this.icon = 'fas fa-exclamation-circle';
        break;
      case 'lock':
        this.icon = 'fas fa-lock';
        break;
      default:
        this.icon = 'fas fa-ban';
    }
  }

  protected readonly DataExportMode = DataExportMode;
}
